import clsx from 'clsx';
import { forwardRef, HTMLProps, useCallback, useId } from 'react';
import { useFormContext } from 'react-hook-form';

import { useOwnTranslate } from 'lib/hooks/useOwnTranslate';

import CheckBox from '../checkBox';

interface Props
  extends Omit<HTMLProps<HTMLInputElement>, 'type' | 'className'> {
  defaultValue?: number | string;
  values: (number | null)[];
  isminormax?: 'fromMin' | 'toMax' | 'none';
  name: string;
  isExact?: boolean;
  isExactName?: string;
}

const IntegerFilterField = forwardRef<HTMLInputElement, Props>(
  ({ values = false, ...rest }, ref) => {
    const { t } = useOwnTranslate('global');
    const methods = useFormContext();
    const isExactFormField = methods.watch(
      rest.isExactName || `isExact_${rest.name}`
    ) as boolean;

    let id = useId();
    if (rest.id) {
      id = rest.id;
    }

    const onItemClickHandler = useCallback(
      (itemValue: number | null) => {
        if (itemValue === rest.defaultValue) {
          // setValue(null);
          methods.setValue(rest.name, null, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          });
          methods.trigger(rest.name);
        } else {
          // setValue(itemValue ?? undefined);

          methods.setValue(rest.name, itemValue, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          });
          methods.trigger(rest.name);
        }
      },
      [rest.defaultValue]
    );

    return (
      <>
        <ul className="inline-flex flex-wrap -m-1">
          {(values as number[])
            .sort((a, b) => (a || Number.MIN_VALUE) - (b || Number.MIN_VALUE))
            .map((item) => (
              <li
                key={item}
                className={clsx(
                  'flex items-center justify-center w-auto max-h-8 min-w-[2rem] rounded-full py-1 px-3 cursor-pointer hover:bg-primary-500 active:bg-primary-500 hover:text-white m-1',
                  {
                    'bg-primary-500 text-grey-100': item === rest.defaultValue,
                    'bg-grey-100 text-grey-700': item !== rest.defaultValue,
                  }
                )}
                onClick={() => onItemClickHandler(item)}
              >
                <span>
                  {item === 0
                    ? isExactFormField
                      ? t('suite')
                      : t('all')
                    : isExactFormField
                      ? item
                      : rest.isminormax !== 'none'
                        ? rest.isminormax === 'fromMin'
                          ? `+${item}`
                          : `-${item}`
                        : item}
                </span>
              </li>
            ))}
        </ul>
        <div className="mt-3">
          <CheckBox
            label={t('exactCount')}
            // defaultChecked={checkIsExact}
            defaultChecked={methods.getValues(
              rest.isExactName || `isExact_${rest.name}`
            )}
            {...methods.register(rest.isExactName || `isExact_${rest.name}`)}
            onChange={(e) => {
              methods.setValue(
                rest.isExactName || `isExact_${rest.name}`,
                e.currentTarget.checked,
                {
                  shouldDirty: true,
                  shouldTouch: true,
                  shouldValidate: true,
                }
              );
            }}
          />
        </div>
        <input
          type="number"
          id={id}
          className="hidden"
          {...methods.register(rest.name)}
          defaultValue={rest.defaultValue}
          {...rest}
          ref={ref}
        />
      </>
    );
  }
);

IntegerFilterField.displayName = 'IntegerFilterField';

export default IntegerFilterField;
